import { useCallback, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Web3Modal from "web3modal"
import { useBalance, useUserProviderAndSigner } from "eth-hooks"
import { ethers, utils } from "ethers"
import { isEmpty } from "lodash"
import { toast } from "react-toastify"
import Web3 from "web3"

import {
  getUserWalletNonce,
  registerWalletAddress,
  setWalletConnected,
  setVerifyingAddress,
} from "store/user/actions"
import { getWalletProviders } from "helpers/wallet"
import { CURRENT_ENV } from "constants/config"

const DEBUG = CURRENT_ENV !== "production"
const web3Modal = new Web3Modal({
  network: "mainnet", // Optional. If using WalletConnect on xDai, change network to "xdai" and add RPC info below for xDai chain.
  cacheProvider: true, // optional
  theme: "light", // optional. Change to "dark" for a dark theme.
})

export const useWallet = (onChangeAddress = false) => {
  const dispatch = useDispatch()

  const isWalletEnabled = false // Disable L2E wallet

  const [injectedProvider, setInjectedProvider] = useState()
  const [modalProvider, setModalProvider] = useState()
  const [web3, setWeb3] = useState()
  const [address, setAddress] = useState()
  const [localProvider, setLocalProvider] = useState()
  const [mainnetProvider, setMainnetProvider] = useState()

  const {
    walletDetails,
    isWalletConnected = false,
    isVerifyingAddress = false,
  } = useSelector((state) => state.userReducer)

  const walletVerified = walletDetails?.verified
  const walletAddress = walletDetails?.address

  // Use your injected provider from 🦊 Metamask or if you don't have it then instantly generate a 🔥 burner wallet.
  const userProviderAndSigner = useUserProviderAndSigner(
    injectedProvider,
    localProvider
  )
  const userSigner = userProviderAndSigner.signer

  const newLocal = localProvider?._network?.chainId
  // You can warn the user if you would like them to be on a specific network
  const localChainId = newLocal
  const selectedChainId = userSigner?.provider?._network?.chainId

  // 🏗 scaffold-eth is full of handy hooks like this one to get your balance:
  const yourLocalBalance = useBalance(localProvider, address)

  // Just plug in different 🛰 providers to get your balance on different chains:
  const yourMainnetBalance = useBalance(mainnetProvider, address)

  const logoutOfWeb3Modal = async () => {
    await web3Modal.clearCachedProvider()
    if (typeof injectedProvider?.provider?.disconnect == "function") {
      await injectedProvider.provider.disconnect()
    }
    if (!isEmpty(modalProvider)) {
      modalProvider.removeAllListeners()
    }
    dispatch(setWalletConnected(false))
    dispatch(setVerifyingAddress(false))
  }

  useEffect(() => {
    if (isWalletEnabled) {
      const { localProvider, mainnetProvider } = getWalletProviders()

      setLocalProvider(localProvider)
      setMainnetProvider(mainnetProvider)
    }
  }, [])

  const loadWeb3Modal = useCallback(async () => {
    const provider = await web3Modal.connect()
    setModalProvider(provider)
    setWeb3(new Web3(provider))
    setInjectedProvider(new ethers.providers.Web3Provider(provider))

    provider.on("chainChanged", (chainId) => {
      console.log(`chain changed to ${chainId}! updating providers`)
      setInjectedProvider(new ethers.providers.Web3Provider(provider))
    })

    provider.on("accountsChanged", (accounts) => {
      console.log(`account changed!`, accounts)
      if (isEmpty(accounts)) {
        logoutOfWeb3Modal()
      }
      setInjectedProvider(new ethers.providers.Web3Provider(provider))
    })

    // Subscribe to session disconnection
    provider.on("disconnect", (code, reason) => {
      console.log(code, reason)
      logoutOfWeb3Modal()
    })
  }, [setInjectedProvider])

  useEffect(() => {
    // Monitor changes to wallet verification
    if (walletVerified && web3) {
      dispatch(setWalletConnected(true))
      dispatch(setVerifyingAddress(false))
      toast.success("Wallet successfully connected")
    }
  }, [walletVerified])

  useEffect(() => {
    async function getAddress() {
      if (userSigner) {
        const newAddress = await userSigner.getAddress()
        setAddress(newAddress)

        if (web3Modal.cachedProvider && web3) {
          if (onChangeAddress) {
            try {
              onChangeAddress(newAddress)
            } catch (e) {
              console.error("Error on onChangeAddress: ", e)
            }
          } else {
            dispatch(setVerifyingAddress(true))

            if (walletAddress === newAddress) {
              if (walletVerified) {
                dispatch(setWalletConnected(true))
                dispatch(setVerifyingAddress(false))
              } else {
                dispatch(getUserWalletNonce({ public_address: newAddress }))
              }
            } else {
              dispatch(
                registerWalletAddress({
                  public_address: newAddress,
                })
              )
            }
          }
        }
      }
    }
    getAddress()
  }, [userSigner])

  useEffect(() => {
    if (web3Modal.cachedProvider && walletVerified) {
      loadWeb3Modal()
    }
  }, [loadWeb3Modal])

  // 🧫 DEBUG 👨🏻‍🔬
  useEffect(() => {
    if (
      DEBUG &&
      mainnetProvider &&
      address &&
      selectedChainId &&
      yourLocalBalance &&
      yourMainnetBalance
    ) {
      console.log(
        "_____________________________________ 🏗 scaffold-eth _____________________________________"
      )
      console.log("🌎 mainnetProvider", mainnetProvider)
      console.log("🏠 localChainId", localChainId)
      console.log("👩‍💼 selected address:", address)
      console.log("🕵🏻‍♂️ selectedChainId:", selectedChainId)
      console.log(
        "💵 yourLocalBalance",
        yourLocalBalance ? ethers.utils.formatEther(yourLocalBalance) : "..."
      )
      console.log(
        "💵 yourMainnetBalance",
        yourMainnetBalance
          ? ethers.utils.formatEther(yourMainnetBalance)
          : "..."
      )
    }
  }, [
    localChainId,
    mainnetProvider,
    address,
    selectedChainId,
    yourLocalBalance,
    yourMainnetBalance,
  ])

  // let walletBalance = parseFloat("1000.00")
  let walletBalance = parseFloat("0.00")
  if (yourLocalBalance) {
    const etherBalance = utils.formatEther(yourLocalBalance)
    parseFloat(etherBalance).toFixed(2)
    walletBalance = parseFloat(etherBalance)
  }

  return {
    walletAddress: address,
    walletBalance,
    isWalletConnected,
    isVerifyingAddress,
    web3,
    loadWeb3Modal,
    logoutOfWeb3Modal,
  }
}
